const {
  locales,
  defaultLocale,
  hreflangOf,
  toContentLocale,
} = require("./locales");

/**
 * WHO-5654. The last path segment of the Pro Buyer seller page, per locale.
 * `pages/pro-buyer/sellers.tsx` is the one route; every other locale serves
 * its own word through a rewrite and 301s the other words onto it.
 *
 * A dependency-free CJS leaf, like `locales.js`: `rewrites.js`,
 * `redirects.js` and `scripts/generate-sitemap.ts` `require()` it, and the
 * feature's static file imports it for the in-app links. The same words are
 * in `lib/multilingual/locales/*.json` (key `sellers`) so Canonical, HrefLang
 * and the language switcher localize the path; a test keeps them in step.
 */
const PRO_BUYER_SELLERS_WORDS = {
  en: "sellers",
  nl: "verkopers",
  de: "verkaeufer",
  fr: "vendeurs",
  it: "venditori",
  es: "vendedores",
};

const PRO_BUYER_BASE = "/pro-buyer";

/** The internal route every locale renders. */
const PRO_BUYER_SELLERS_PAGE = `${PRO_BUYER_BASE}/${PRO_BUYER_SELLERS_WORDS[defaultLocale]}`;

/**
 * The page's path in a locale, without the locale prefix. `en-us` uses the English word (WHO-5766);
 * unknown locales get the default.
 */
const getProBuyerSellersPath = locale =>
  `${PRO_BUYER_BASE}/${
    PRO_BUYER_SELLERS_WORDS[toContentLocale(locale)] ??
    PRO_BUYER_SELLERS_WORDS[defaultLocale]
  }`;

/** The public path including the locale prefix (none for the default locale). */
const getProBuyerSellersPublicPath = locale =>
  locale && locale !== defaultLocale
    ? `/${locale}${getProBuyerSellersPath(locale)}`
    : getProBuyerSellersPath(defaultLocale);

const prefixedLocales = locales.filter(locale => locale !== defaultLocale);

/** `/nl/pro-buyer/verkopers` serves `/nl/pro-buyer/sellers`. */
const proBuyerSellersRewrites = () =>
  prefixedLocales
    // `/en-us` uses the English word, which is the route itself: nothing to rewrite.
    .filter(locale => getProBuyerSellersPath(locale) !== PRO_BUYER_SELLERS_PAGE)
    .map(locale => ({
      source: `/${locale}${getProBuyerSellersPath(locale)}`,
      destination: `/${locale}${PRO_BUYER_SELLERS_PAGE}`,
      locale: false,
    }));

/**
 * `/nl/pro-buyer/sellers` and `/nl/pro-buyer/vendeurs` 301 to
 * `/nl/pro-buyer/verkopers`, so no locale answers 200 on two URLs. Only
 * mismatched pairs are generated, so the pair each rewrite serves is never
 * redirected. The default locale gets no rule: `/en/`-prefixed sources are
 * forbidden (WHO-5477, `redirects.default-locale.test.ts`) and English is
 * unprefixed `/pro-buyer/sellers`, which is the route itself.
 */
const proBuyerSellersRedirects = () =>
  prefixedLocales.flatMap(locale => {
    const own = PRO_BUYER_SELLERS_WORDS[toContentLocale(locale)];

    return [...new Set(Object.values(PRO_BUYER_SELLERS_WORDS))]
      .filter(word => word !== own)
      .map(word => ({
        source: `/${locale}${PRO_BUYER_BASE}/${word}`,
        destination: `/${locale}${PRO_BUYER_BASE}/${own}`,
        permanent: true,
        locale: false,
      }));
  });

/**
 * Sitemap entries: one `<url>` per locale at its localized path, each
 * carrying the full hreflang cluster plus x-default (the default locale).
 */
const proBuyerSellersSitemapEntries = canonicalHost => {
  const alternates = [
    {
      hreflang: "x-default",
      href: `${canonicalHost}${getProBuyerSellersPublicPath(defaultLocale)}`,
    },
    ...locales.map(locale => ({
      hreflang: hreflangOf(locale),
      href: `${canonicalHost}${getProBuyerSellersPublicPath(locale)}`,
    })),
  ];

  return locales.map(locale => ({
    loc: getProBuyerSellersPublicPath(locale),
    alternates,
  }));
};

module.exports = {
  proBuyerSellersSitemapEntries,
  PRO_BUYER_SELLERS_WORDS,
  PRO_BUYER_SELLERS_PAGE,
  getProBuyerSellersPath,
  getProBuyerSellersPublicPath,
  proBuyerSellersRewrites,
  proBuyerSellersRedirects,
};
